// ---------------------------------------------------------------------------
// AudioPlayer — Inline HTML5 audio player with play/pause
// ---------------------------------------------------------------------------

import { useRef, useState } from 'react';
import { Play, Pause } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getApiBaseUrl } from '@/lib/config';

interface AudioPlayerProps {
  s3Key: string;
}

export function AudioPlayer({ s3Key }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const audioUrl = `${getApiBaseUrl()}/audio/stream/${encodeURIComponent(s3Key)}`;

  function togglePlay() {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
    } else {
      el.play();
    }
  }

  return (
    <div className="flex items-center gap-2">
      <audio
        ref={audioRef}
        src={audioUrl}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setProgress(0); }}
        onTimeUpdate={() => {
          const el = audioRef.current;
          if (el && el.duration) {
            setProgress((el.currentTime / el.duration) * 100);
          }
        }}
      />
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0"
        onClick={togglePlay}
      >
        {playing ? (
          <Pause className="h-3.5 w-3.5 text-purple-600" />
        ) : (
          <Play className="h-3.5 w-3.5 text-purple-600" />
        )}
      </Button>
      <div className="flex-1 h-1.5 bg-silver-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-purple-500 rounded-full transition-all duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}
